import type { Key } from 'react';
import { useAtomValue } from 'jotai';
import type { QuestionnairePayloadT } from '../../../../../common/side-panel/analytics/types';
import { partitionStaticAndDynamicQuestionnaire } from '../../../../../common/side-popover-panel/utils';
import type { AnnotationLabelT, AnswerT } from '../../../../../common/types/types';
import type { CachedQuestionnaireStatus } from '../../../../../modernization-common/side-panel/atoms/questionnaire.atom';
import { filterDynamicAnswers, filterStaticAnswers } from '../../../../labelling/reducers/utils/utils';
import { annotationLabelsAtom, annotationMetadataAtom, annotationUIStateAtom } from '../../../atoms/annotation-atoms';
import type { AnnotationMetadataRecord, AnnotationUIStateRecord } from '../../../types/annotation';
import type { SelectedAnnotationDetailsT } from '../../side-panel/types';
export const PANEL_KEYS = {
  METADATA: 'metadata',
  INSTANCES: 'instances',
  RELATIONSHIPS: 'relationships'
};
export const getAutoExpandedPanels = ({
  defaultExpandedPanels,
  hasQuestionnaire,
  isVideoTask
}: {
  defaultExpandedPanels?: Key[];
  hasQuestionnaire: boolean;
  isVideoTask: boolean;
}): Key[] => {
  if (defaultExpandedPanels && defaultExpandedPanels.length) {
    return defaultExpandedPanels;
  }
  if (hasQuestionnaire) {
    return [PANEL_KEYS.METADATA];
  }
  if (isVideoTask) {
    return [PANEL_KEYS.INSTANCES];
  }
  return [];
};
export const shouldPushCollapsedPanelToBottom = ({
  panelKey,
  visiblePanelKeys,
  expandedPanelKeys
}: {
  panelKey: Key;
  visiblePanelKeys: Key[];
  expandedPanelKeys: Key[];
}): boolean => {
  const firstCollapsedPanelAfterExpanded = visiblePanelKeys.find((visiblePanelKey, index) => {
    if (expandedPanelKeys.includes(visiblePanelKey)) {
      return false;
    }
    return visiblePanelKeys.slice(0, index).some(previousPanelKey => expandedPanelKeys.includes(previousPanelKey));
  });
  return firstCollapsedPanelAfterExpanded === panelKey;
};
const hasCompletedAnswer = (answers: AnswerT | undefined, questionId: string): boolean => {
  return answers?.[questionId]?.answers.some(answer => typeof answer === 'string' ? answer.trim().length > 0 : Boolean(answer)) ?? false;
};
export const getMetadataQuestionnaireTagLabel = ({
  isStaticQuestionnairePending,
  questionnaireAnswers,
  selectedQuestionnaire
}: {
  isStaticQuestionnairePending: boolean;
  questionnaireAnswers?: AnswerT;
  selectedQuestionnaire?: CachedQuestionnaireStatus;
}): string => {
  if (!isStaticQuestionnairePending) {
    return 'Complete';
  }
  const staticQuestionnaire = selectedQuestionnaire?.status === 'success' ? partitionStaticAndDynamicQuestionnaire(selectedQuestionnaire.questionnaire).staticQuestionnaire : undefined;
  const totalStaticQuestions = staticQuestionnaire?.topQuestionIds.length ?? 0;
  const completedStaticQuestions = staticQuestionnaire?.topQuestionIds.filter(questionId => hasCompletedAnswer(questionnaireAnswers, questionId)).length ?? 0;
  return `${completedStaticQuestions}/${totalStaticQuestions}`;
};
export const getSelectedAnnotationDetails = ({
  selectedAnnotationId,
  annotationLabels,
  annotationMetadata,
  annotationUIState
}: {
  selectedAnnotationId?: string;
  annotationLabels: AnnotationLabelT[];
  annotationMetadata: AnnotationMetadataRecord;
  annotationUIState: AnnotationUIStateRecord;
}): SelectedAnnotationDetailsT | undefined => {
  if (!selectedAnnotationId) {
    return undefined;
  }
  const metadata = annotationMetadata.get(selectedAnnotationId);
  if (!metadata) {
    return undefined;
  }
  const annotationLabel = annotationLabels.find(label => label.title === metadata.labelId) ?? {
    title: metadata.labelId,
    style: {
      strokeColor: '',
      fillColor: '',
      fillOpacity: 0,
      strokeWidth: 0
    }
  };
  const uiState = annotationUIState.get(selectedAnnotationId);
  return {
    id: selectedAnnotationId,
    annotationType: metadata.annotationType,
    annotationLabel,
    metadata: {
      answers: metadata.answers
    },
    locked: uiState?.locked ?? false,
    hidden: uiState?.hidden ?? false
  };
};
export const useSelectedAnnotationDetails = (annotationId: string): SelectedAnnotationDetailsT | undefined => {
  const annotationLabels = useAtomValue(annotationLabelsAtom);
  const annotationMetadata = useAtomValue(annotationMetadataAtom);
  const annotationUIState = useAtomValue(annotationUIStateAtom);
  return getSelectedAnnotationDetails({
    selectedAnnotationId: annotationId,
    annotationLabels,
    annotationMetadata,
    annotationUIState
  });
};
export const getQuestionnaireEventPayload = (annotation: SelectedAnnotationDetailsT): QuestionnairePayloadT => {
  return {
    objectId: annotation.id,
    objectType: annotation.annotationType,
    label: annotation.annotationLabel.title
  };
};
export const getUpdatedAnnotationDetailsWithQuestionnaireAnswers = ({
  selectedAnnotationDetails,
  answers
}: {
  selectedAnnotationDetails: SelectedAnnotationDetailsT;
  answers?: AnswerT;
}): SelectedAnnotationDetailsT => {
  return {
    ...selectedAnnotationDetails,
    metadata: {
      ...selectedAnnotationDetails.metadata,
      answers: {
        ...selectedAnnotationDetails.metadata?.answers,
        ...filterDynamicAnswers(answers),
        ...filterStaticAnswers(answers)
      }
    }
  };
};